import { useState } from 'react';
import { Button, Confirm, useRecordContext, useNotify } from 'react-admin';
import { Box } from '@mui/material';
import { client } from '../../../data/api';
import gql from 'graphql-tag';
import LoadingOverlay from 'src/components/LoadingOverlay';
import { DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import { getOrgId, generateErrorMessage, buttonStyles } from 'src/utils/organizationSubscriptions';
import { useMutation } from '@apollo/client';

export const DeleteAll = ({ refresh }: { refresh: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const record = useRecordContext();
  const notify = useNotify();
  const buttonClasses = buttonStyles();

  const [deleteAllCommodityCompanyMappings] = useMutation(
    gql`
      mutation alertsDeleteAllCommodityCompanyMappingsByOrgId($organizationId: ID!) {
        alertsDeleteAllCommodityCompanyMappingsByOrgId(organizationId: $organizationId) {
          ... on AlertsServiceAlertsDeleteAllCommodityCompanyMappingsPayload {
            deletedCount
            datetime
          }
          ... on AlertsServiceConnectedObjectNotFoundError {
            message
          }
        }
      }
    `,
    {
      client,
      errorPolicy: 'all',
    },
  );

  const handleDeleteAll = async () => {
    const orgId = record?.org_id || getOrgId();
    if (!orgId) {
      setOpen(false);
      setIsLoading(false);
      return notify('No organization id found');
    }
    try {
      // First, get all company commodity records for the organization from the old service
      const { data: queryData } = await client.query<{
        signals_company_commodities: { id: number; commodity_id: string; company_id: number }[];
      }>({
        query: gql`
          query GetAllCompanyCommodityRecords($orgId: bigint!) {
            signals_company_commodities(where: { org_id: { _eq: $orgId } }) {
              id
              commodity_id
              company_id
            }
          }
        `,
        variables: { orgId: parseInt(orgId, 10) },
      });

      const recordsToDelete = queryData?.signals_company_commodities || [];
      let errorMessage = '';

      if (recordsToDelete.length > 0) {
        const { data: alertsServiceData } = await deleteAllCommodityCompanyMappings({
          variables: {
            organizationId: orgId,
          },
        });

        const realData = alertsServiceData?.alertsDeleteAllCommodityCompanyMappingsByOrgId;

        if (realData?.__typename === 'AlertsServiceConnectedObjectNotFoundError') {
          errorMessage = realData.message;
        }
      }

      // Delete from old service
      const { data } = await client.mutate<{
        signals_delete_company_commodities: { affected_rows: number };
      }>({
        mutation: gql`
          mutation DeleteAllCompanyCommodities($where: company_commodities_bool_exp!) {
            signals_delete_company_commodities(where: $where) {
              affected_rows
            }
          }
        `,
        variables: {
          where: {
            org_id: {
              _eq: parseInt(orgId, 10),
            },
          },
        },
      });

      setIsLoading(false);
      setOpen(false);
      const numAffectedRows = data?.signals_delete_company_commodities?.affected_rows || 0;
      notify(
        `${numAffectedRows.toLocaleString()} company commodities deleted${errorMessage ? `\n${errorMessage}` : ''}`,
      );

      return refresh();
    } catch (error) {
      setOpen(false);
      setIsLoading(false);
      const errorMessage = generateErrorMessage({
        error,
        prependMessage: 'Error deleting company commodities\n',
      });
      return notify(errorMessage, { type: 'error' });
    }
  };
  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = async () => {
    setIsLoading(true);
    await handleDeleteAll();
  };

  return (
    <Box sx={{ display: 'block', margin: 0, px: '0.25rem', py: '0.5rem', minWidth: '8.5rem' }}>
      <>
        <Button label="Delete All" onClick={handleClick} size="medium" className={buttonClasses.deleteButton}>
          <DeleteForeverIcon />
        </Button>
        {!isLoading && (
          <Confirm
            isOpen={open}
            title={`Delete all company commodities for Org Id #${record && record.id}`}
            content="Are you sure you want to delete all company commodities for this organization?"
            onConfirm={handleConfirm}
            onClose={handleDialogClose}
            confirm="Delete All"
            className={buttonClasses.confirmDeleteButton}
          />
        )}
      </>

      {isLoading && <LoadingOverlay open />}
    </Box>
  );
};

export default DeleteAll;
